// Space storm visuals: a fog-of-war overlay with a clear vision radius around
// the player, a full-screen thunder flash (double-strobe decay), and jagged
// firebolt lightning strikes from the sky with an impact flash. All animation
// keys off the sim tick (tickF = world.tick + alpha), never the wall clock,
// so replays, pause, and time dilation behave identically to gameplay. The
// thunder flash goes through the photosafety FlashGovernor (see
// flash-governor.ts), also on the sim-tick clock.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';
import { wrappedDelta } from '../sim/collision.js';
import type { SpaceStormRenderState } from '../sim/space-storm.js';
import { FlashGovernor } from './flash-governor.js';

/** Fog eases in/out over ~0.6 s at 60 Hz when the storm starts/ends. */
const FOG_FADE_TICKS = 36;
/** Bolt visual lifetime (~0.2 s); shorter than the sim's strike linger. */
const BOLT_TICKS = 12;
/** Thunder envelope span in ticks (ages 0..11) — its natural flash duration. */
const THUNDER_TICKS = 12;
const THUNDER_S = THUNDER_TICKS / 60;
/** Natural thunder flash peak; the governor clamps what it admits. */
const THUNDER_PEAK = 0.85;
/** Sky height the bolt drops from (above the view top edge). */
const BOLT_HEIGHT = 26;
/** Pooled bolt slots; the storm never has more than a couple alive at once. */
const MAX_BOLTS = 8;
/** Line segments per bolt (jagged polyline). */
const BOLT_SEGMENTS = 12;
const VERTS_PER_BOLT = BOLT_SEGMENTS * 2;

// Deterministic one-shot jitter in [-0.5, 0.5): mulberry32 core, same hash
// family as the sim's RngStream. Bolt shape is keyed on the strike tick, so a
// strike renders identically on every frame and every replay.
function boltJitter(seed: number): number {
  let t = (seed + 0x6D2B79F5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296 - 0.5;
}

export class SpaceStormFX {
  readonly group: THREE.Group;
  private readonly fog: THREE.Mesh;
  private readonly fogMaterial: THREE.ShaderMaterial;
  private readonly thunder: THREE.Mesh;
  private readonly thunderMaterial: THREE.MeshBasicMaterial;
  private readonly bolts: THREE.LineSegments;
  private readonly boltPositions: Float32Array;
  private readonly boltColors: Float32Array;
  private readonly impactFlashes: THREE.Mesh[] = [];

  // Fog fade envelope (same tick-stamped pattern as the bonus tint).
  private fogActive = false;
  private fogStrength = 0;
  private fogChangeTick = 0;
  private fogChangeFrom = 0;

  // Photosafety governor for the full-screen thunder flash: each fresh
  // thunder tick is one request. Refused flashes never play; admitted ones
  // run the envelope at the governor-clamped intensity and duration.
  private readonly flashGovernor = new FlashGovernor();
  private thunderGovernedTick = -1000;
  private thunderPeak = 0;
  private thunderTimeScale = 1;
  private prevThunderTickF = 0;

  /** Current eased fog strength (0..1) — drives storm edge-blur boosting. */
  get fogStrengthNow(): number {
    return this.fogStrength;
  }

  constructor() {
    this.group = new THREE.Group();

    // Fog of war: camera-anchored quad, dark everywhere except a soft-edged
    // vision circle around the player (who renders at the camera anchor).
    this.fogMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uStrength: { value: 0 },
        uVisionRadius: { value: 12 },
        uHalfSize: { value: new THREE.Vector2(40, 26) },
        uTime: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uStrength;
        uniform float uVisionRadius;
        uniform vec2 uHalfSize;
        uniform float uTime;
        varying vec2 vUv;

        void main() {
          if (uStrength <= 0.004) discard;
          // World units from the quad center (= the player position).
          vec2 p = (vUv * 2.0 - 1.0) * uHalfSize;
          float rad = length(p);
          float th = atan(p.y, p.x);
          // Slow drifting edge wobble so the fog boundary reads as vapor.
          float wobble = sin(th * 5.0 + uTime * 0.9) * 0.5
                       + sin(th * 9.0 - uTime * 1.3) * 0.3;
          float outside = smoothstep(uVisionRadius * 0.8, uVisionRadius * 1.25 + wobble, rad + wobble);
          // Gloom inside, near-blackout beyond the vision radius — the storm
          // must swallow the edges, not just dim them.
          float alpha = uStrength * mix(0.3, 0.985, outside);
          gl_FragColor = vec4(vec3(0.008, 0.012, 0.045), alpha);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
    this.fog = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.fogMaterial);
    this.fog.frustumCulled = false;
    this.fog.renderOrder = 3;
    this.fog.visible = false;
    this.group.add(this.fog);

    // Firebolt bolts: one pooled LineSegments mesh, per-vertex color fades.
    this.boltPositions = new Float32Array(MAX_BOLTS * VERTS_PER_BOLT * 3);
    this.boltColors = new Float32Array(MAX_BOLTS * VERTS_PER_BOLT * 3);
    const boltGeo = new THREE.BufferGeometry();
    boltGeo.setAttribute('position', new THREE.BufferAttribute(this.boltPositions, 3));
    boltGeo.setAttribute('color', new THREE.BufferAttribute(this.boltColors, 3));
    const boltMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
      depthTest: false,
      depthWrite: false,
    });
    this.bolts = new THREE.LineSegments(boltGeo, boltMat);
    this.bolts.frustumCulled = false;
    this.bolts.renderOrder = 4;
    this.bolts.visible = false;
    this.group.add(this.bolts);

    // Impact flash pool: one small additive quad per bolt slot.
    const flashGeo = new THREE.PlaneGeometry(1, 1);
    for (let i = 0; i < MAX_BOLTS; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xdff4ff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
        depthTest: false,
        depthWrite: false,
      });
      const quad = new THREE.Mesh(flashGeo, mat);
      quad.frustumCulled = false;
      quad.renderOrder = 4;
      quad.visible = false;
      this.impactFlashes.push(quad);
      this.group.add(quad);
    }

    // Thunder flash: full-screen white quad, opacity from the flash envelope.
    this.thunderMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
      depthTest: false,
      depthWrite: false,
    });
    this.thunder = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.thunderMaterial);
    this.thunder.frustumCulled = false;
    this.thunder.renderOrder = 5;
    this.thunder.visible = false;
    this.group.add(this.thunder);
  }

  /**
   * Per-frame update. `state` is the sim's storm snapshot (null = no storm);
   * tickF = world.tick + alpha drives every envelope; viewW/viewH are the
   * camera's world-space extents used to size the overlay quads.
   */
  update(
    state: SpaceStormRenderState | null,
    cameraAnchor: Vec2,
    tickF: number,
    viewW: number,
    viewH: number,
    reducedFlashes: boolean
  ): void {
    this.updateFog(state, cameraAnchor, tickF, viewW, viewH);
    this.updateThunder(state, cameraAnchor, tickF, viewW, viewH, reducedFlashes);
    this.updateBolts(state, cameraAnchor, tickF);
  }

  dispose(): void {
    this.fog.geometry.dispose();
    this.fogMaterial.dispose();
    this.thunder.geometry.dispose();
    this.thunderMaterial.dispose();
    this.bolts.geometry.dispose();
    (this.bolts.material as THREE.Material).dispose();
    for (const quad of this.impactFlashes) {
      (quad.material as THREE.Material).dispose();
    }
    // Shared impact-flash geometry; dispose once via the first quad.
    if (this.impactFlashes.length > 0) this.impactFlashes[0].geometry.dispose();
  }

  private updateFog(
    state: SpaceStormRenderState | null,
    cameraAnchor: Vec2,
    tickF: number,
    viewW: number,
    viewH: number
  ): void {
    const active = state?.active === true;
    if (active !== this.fogActive) {
      this.fogActive = active;
      this.fogChangeTick = Math.floor(tickF);
      this.fogChangeFrom = this.fogStrength;
    }
    if (tickF < this.fogChangeTick) {
      // Tick went backwards (run restarted): rebase the fade on this tick.
      this.fogChangeTick = Math.floor(tickF);
      this.fogChangeFrom = this.fogStrength;
    }
    const k = Math.min(1, Math.max(0, tickF - this.fogChangeTick) / FOG_FADE_TICKS);
    const eased = k * k * (3 - 2 * k);
    const target = this.fogActive ? 1 : 0;
    this.fogStrength = this.fogChangeFrom + (target - this.fogChangeFrom) * eased;

    const visible = this.fogStrength > 0.004;
    this.fog.visible = visible;
    if (!visible) return;
    this.fog.position.set(cameraAnchor.x, cameraAnchor.y, 0.2);
    this.fog.scale.set(viewW + 8, viewH + 8, 1);
    this.fogMaterial.uniforms.uStrength.value = this.fogStrength;
    this.fogMaterial.uniforms.uVisionRadius.value = state?.visionRadius ?? 12;
    (this.fogMaterial.uniforms.uHalfSize.value as THREE.Vector2).set(
      (viewW + 8) * 0.5,
      (viewH + 8) * 0.5
    );
    this.fogMaterial.uniforms.uTime.value = tickF / 60;
  }

  private updateThunder(
    state: SpaceStormRenderState | null,
    cameraAnchor: Vec2,
    tickF: number,
    viewW: number,
    viewH: number,
    reducedFlashes: boolean
  ): void {
    if (tickF < this.prevThunderTickF) {
      // Tick went backwards (run restarted): drop the old run's stamps so
      // they can't refuse the new run's first flashes.
      this.flashGovernor.reset();
      this.thunderGovernedTick = -1000;
    }
    this.prevThunderTickF = tickF;
    this.flashGovernor.reduced = reducedFlashes;

    if (state && state.lastThunderTick !== this.thunderGovernedTick) {
      this.thunderGovernedTick = state.lastThunderTick;
      const age = tickF - state.lastThunderTick;
      // Only a fresh thunder tick spends rate budget — the sim's -1000
      // "never" sentinel and long-past ticks produce no light either way.
      if (
        age >= 0 &&
        age <= THUNDER_TICKS &&
        this.flashGovernor.request(tickF / 60, THUNDER_PEAK, THUNDER_S)
      ) {
        this.thunderPeak = this.flashGovernor.luminance;
        // Squeeze the envelope into the admitted duration, keeping the
        // double-strobe shape.
        this.thunderTimeScale = THUNDER_S / this.flashGovernor.durationS;
      } else {
        this.thunderPeak = 0;
      }
    }

    const flash =
      state && this.thunderPeak > 0
        ? this.thunderEnvelope((tickF - state.lastThunderTick) * this.thunderTimeScale)
        : 0;
    const strength = flash * this.thunderPeak;
    const visible = strength > 0.004;
    this.thunder.visible = visible;
    if (!visible) return;
    this.thunder.position.set(cameraAnchor.x, cameraAnchor.y, 0.3);
    this.thunder.scale.set(viewW + 8, viewH + 8, 1);
    this.thunderMaterial.opacity = strength;
  }

  /** Double-strobe decay after a thunder tick: fast primary, weaker echo. */
  private thunderEnvelope(age: number): number {
    if (age < 0 || age > 11) return 0;
    let f = 0;
    if (age <= 5) f = 1 - age / 5;
    if (age >= 7) f = Math.max(f, 0.5 * (1 - (age - 7) / 4));
    return f;
  }

  private updateBolts(state: SpaceStormRenderState | null, cameraAnchor: Vec2, tickF: number): void {
    const strikes = state?.strikes ?? [];
    const count = Math.min(strikes.length, MAX_BOLTS);
    let anyVisible = false;

    for (let slot = 0; slot < MAX_BOLTS; slot++) {
      const strike = slot < count ? strikes[slot] : undefined;
      const age = strike ? tickF - strike.tick : Infinity;
      const fade = strike && age >= 0 && age < BOLT_TICKS ? 1 - age / BOLT_TICKS : 0;
      const flash = this.impactFlashes[slot];

      if (!strike || fade <= 0) {
        if (flash.visible) {
          flash.visible = false;
          (flash.material as THREE.MeshBasicMaterial).opacity = 0;
        }
        this.writeBolt(slot, 0, 0, 0, 0, 0, 0, 0, 0);
        continue;
      }
      anyVisible = true;

      // Unwrap the strike into the camera frame like any other entity.
      const d = wrappedDelta(strike.pos, cameraAnchor);
      const bx = cameraAnchor.x + d.x;
      const by = cameraAnchor.y + d.y;

      // Player-targeted bolts read warm; enemy strikes read ice-blue.
      const cr = strike.targeted_player ? 1.0 : 0.72;
      const cg = strike.targeted_player ? 0.72 : 0.9;
      const cb = strike.targeted_player ? 0.5 : 1.0;
      // Tick-derived flicker on top of the linear fade.
      const flicker = 0.8 + 0.2 * Math.sin(age * 2.7);
      const brightness = fade * flicker;

      const seedBase = strike.tick * 977;
      const slant = boltJitter(seedBase) * 8;
      this.writeBolt(slot, bx, by, slant, brightness, cr, cg, cb, seedBase);

      // Impact flash: bright pop that expands as it fades.
      flash.visible = true;
      flash.position.set(bx, by, 0.24);
      const s = strike.radius * 2 * (0.4 + 0.6 * (1 - fade));
      flash.scale.set(s, s, 1);
      const mat = flash.material as THREE.MeshBasicMaterial;
      mat.opacity = fade * fade * 0.9;
      mat.color.setRGB(cr, cg, cb);
    }

    this.bolts.visible = anyVisible;
    if (anyVisible || count > 0) {
      (this.bolts.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      (this.bolts.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    }
  }

  /** Write one jagged bolt (top → strike point) into the pooled buffers. */
  private writeBolt(
    slot: number,
    bx: number,
    by: number,
    slant: number,
    brightness: number,
    cr: number,
    cg: number,
    cb: number,
    seedBase: number
  ): void {
    const topX = bx + slant;
    const topY = by + BOLT_HEIGHT;
    const posBase = slot * VERTS_PER_BOLT * 3;
    for (let i = 0; i < BOLT_SEGMENTS; i++) {
      for (let end = 0; end < 2; end++) {
        const t = (i + end) / BOLT_SEGMENTS;
        // Perpendicular jitter peaks mid-bolt and vanishes at both ends.
        const amp = Math.sin(Math.PI * t);
        const jx = boltJitter(seedBase + (i + end) * 31 + 1) * 2.2 * amp;
        const jy = boltJitter(seedBase + (i + end) * 31 + 2) * 0.9 * amp;
        const v = posBase + (i * 2 + end) * 3;
        this.boltPositions[v] = topX + (bx - topX) * t + jx;
        this.boltPositions[v + 1] = topY + (by - topY) * t + jy;
        this.boltPositions[v + 2] = 0.25;
        // Head of the bolt slightly dimmer, impact end at full brightness.
        const b = brightness * (0.6 + 0.4 * t);
        this.boltColors[v] = cr * b;
        this.boltColors[v + 1] = cg * b;
        this.boltColors[v + 2] = cb * b;
      }
    }
  }
}
